import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import * as L from 'leaflet';
import { LayerExtent } from '../interfaces/layer-extent';
import { CustomMapsService } from '../custom-maps.service';
import { LayerFieldOptionWms } from '../interfaces/layer-field-option-wms';
import { LayerFieldOptionWmts } from '../interfaces/layer-field-options-wmts';
import {catchError, filter, finalize, takeUntil, tap} from 'rxjs/operators';
import { LayerCoordinates } from '../interfaces/layer-coordinates';
import { of, Subject } from 'rxjs';
import { CustomMap } from '../interfaces/custom-map.type';
import {UntypedFormControl} from '@angular/forms';

@Component({
  selector: 'mee-map-preview',
  templateUrl: './map-preview.component.html',
  styleUrls: ['./map-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapPreviewComponent implements AfterViewInit, OnDestroy {
  localSelectedLayer: LayerFieldOptionWms | LayerFieldOptionWmts;

  @Input() isUpdateMode: boolean;

  isTileLoading = false;

  previewKeyData: Record<string, string>;

  polygonCoordinates: any;

  drawnRectangle: any;

  customTiles: any;

  private map: any;

  private destroy$ = new Subject<void>();

  opacityControl = new UntypedFormControl();
  opacityControlConfig = {
    min: 0,
    max: 1,
    step: -0.1,
  }

  @Input() customMap: Partial<CustomMap>;

  @Output() onExpandedChange = new EventEmitter<boolean>();

  @Input() set selectedLayer(selectedLayer: LayerFieldOptionWms | LayerFieldOptionWmts) {
    if (selectedLayer !== undefined && selectedLayer !== null) {
      this.localSelectedLayer = selectedLayer;

      this.getTileImageUrl();

      if (this.drawnRectangle !== undefined) {
        this.map.removeLayer(this.drawnRectangle);
      }
    }
  }

  get selectedLayer() {
    return this.localSelectedLayer;
  }

  constructor(private cdr: ChangeDetectorRef, private customMapsService: CustomMapsService) {}

  ngAfterViewInit() {
    this.map = L.map('map').setView([41.244772343082076, -96.6796875], 2);

    this.opacityControl.setValue(1);
    this.opacityControl.valueChanges.pipe(
      filter(() => !!this.customTiles),
      tap((value) => this.customTiles.setOpacity(value)),
      takeUntil(this.destroy$)
    ).subscribe();

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '<a class="tile-attribution" href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a>',
    }).addTo(this.map);
  }

  private getTileId(zoom) {
    const bounds = this.getBounds();

    const centerCoordinates = bounds.getCenter();

    const lng = centerCoordinates.lng;
    const lat = centerCoordinates.lat;

    const x = Math.floor(((lng + 180) / 360) * Math.pow(2, zoom));

    const y = Math.floor(
      ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * Math.pow(2, zoom),
    );

    return `${zoom}/${x}/${y}`;
  }

  private getGeoJsonFormatCoordinates(extent: LayerExtent): number[][][] {
    const polygonCoordinates = [];

    Object.values(extent).map((layer: LayerCoordinates) => {
      const layerLatLng = [layer.Longitude, layer.Latitude];

      polygonCoordinates.push(layerLatLng);
    });

    this.polygonCoordinates = polygonCoordinates;

    return [polygonCoordinates];
  }

  private getPolygonCoordinates(): number[][] {
    const northWest = this.selectedLayer.Extent['NorthWest'];
    const northEast = this.selectedLayer.Extent['NorthEast'];
    const southEast = this.selectedLayer.Extent['SouthEast'];
    const southWest = this.selectedLayer.Extent['SouthWest'];

    const point0 = [northWest.Latitude, northWest.Longitude];
    const point1 = [northEast.Latitude, northEast.Longitude];
    const point2 = [southEast.Latitude, southEast.Longitude];
    const point3 = [southWest.Latitude, southWest.Longitude];

    return [point0, point1, point2, point3];
  }

  private getBounds() {
    const geoJsonCoordinatesData = {
      type: 'Polygon',
      coordinates: this.getGeoJsonFormatCoordinates(this.selectedLayer.Extent),
    };

    return L.geoJSON(geoJsonCoordinatesData).getBounds();
  }

  private getTileImageUrl() {
    this.isTileLoading = true;

    this.customMapsService
      .getTilePreviewKey(this.customMap.CompanySlug, this.customMap)
      .pipe(
        tap(previewKeyData => {
          this.previewKeyData = previewKeyData;

          const polygonCoordinates = this.getPolygonCoordinates();

          this.drawnRectangle = L.rectangle(polygonCoordinates, { fill: false }).addTo(this.map);

          this.map.fitBounds(this.drawnRectangle.getBounds());

          // forbid to zoom out

          const minZoom = this.map.getZoom();

          if (minZoom < 20) {
            this.map.setMinZoom(minZoom);
          }

          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(this.map);

          this.getCustomTilesOverlay();
        }),
        catchError(error => of(error)),
        finalize(() => {
          this.isTileLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private getCustomTilesOverlay() {
    const polygonCoordinates = this.getPolygonCoordinates();

    const rectangleBounds = L.rectangle(polygonCoordinates).getBounds();

    const tileId = this.getTileId(this.map.getZoom());

    const url = this.customMapsService.getCustomTileRequestUrl(this.customMap.CompanySlug, tileId, this.previewKeyData.Data);

    this.customTiles = L.tileLayer(url, { bounds: rectangleBounds }).addTo(this.map);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
